import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Tools|Bulk Merge';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	feature: HubFeature.MergeCandidatesBulkMergeTool,
	portal: 'SP',
};

const htmlContent = `
<p>
Follow the instructions below to format a CSV file that will allow you to process multiple merges in bulk.  \
Select the CSV file then press the submit button to receive a CSV output file with the merge status.   \
</p>
<p><strong>To create the Comma Separated File (CSV) import file: </strong></p>
<p>
Include the following column headers recognized by the parser. All fields are required for a successful upload. Extra fields will be ignored. Omitting the required columns will result in an error.
</p>
<ul>
	<li>WinnerID</li>
	<li>LoserID</li>
</ul>
<p>The accepted value for an ID is a valid CertMetrics ID (AWS########).</p>
<p>Note:</p>
<ul>
    <li>This tool will only allow a user to select a winner profile and a loser profile, if any specific loser characteristics need to go to the winner account, the merge will have to be done individually via the existing merge page.</li>
    <li>If 3+ accounts need to be merged, the winner must be the same between each merge. The file cannot support the same AWS ID being in both the winner and loser columns.</li>
</ul>
<p><strong>Return CSV file: </strong></p>
<p>After pressing the submit button, a CSV file will be returned with the following columns:</p>
<ul>
	<li>WinnerID</li>
	<li>LoserID</li>
	<li>Merge Status</li>
</ul>
<p>The status field will contain one of these values:</p>
<ul>
	<li>Merge Request Failed</li>
	<li>Merge Successfully Requested</li>
</ul>
`;

export type SPBulkMergeLocNames =
	| 'SPBulkMergePageTitle'
	| 'SPBulkMergePageHeading'
	| 'SPBulkMergeButton'
	| 'SPBulkMergeInstructions'
	| 'SPBulkMergeInvalidRowsError';

export const spBulkMergeLocs: LocDefs<SPBulkMergeLocNames> = {
	SPBulkMergePageTitle: {
		defaultValue: 'Bulk Merges',
		description: 'Page title for the bulk merge page',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPBulkMergePageHeading: {
		defaultValue: 'Candidate Tools',
		description: 'Page Heading for the bulk merge page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPBulkMergeInstructions: {
		defaultValue: htmlContent,
		description: 'Instructions for the bulk merge page',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		htmlAllowed: true,
		...sharedProperties,
	},
	SPBulkMergeButton: {
		defaultValue: 'Merge',
		description: 'Button to merge candidates',
		displayPath: `${dpRoot}|Authorize Button`,
		displayType: 'Button',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPBulkMergeInvalidRowsError: {
		defaultValue: 'No valid rows found.',
		description: 'Error shown when no valid rows are found in the CSV file',
		displayPath: `${dpRoot}|Invalid Rows Error`,
		displayType: 'Error',
		htmlAllowed: false,
		...sharedProperties,
	},
};

export const BULK_MERGE_LOC_KEYS = MapLocKeys<SPBulkMergeLocNames>(spBulkMergeLocs);
export type SPBulkMergeLocType = LocMap<SPBulkMergeLocNames>;
